import { inject, Injectable } from '@angular/core';
import {
  HttpClient,
  HttpEvent,
  HttpHeaders,
  HttpParams,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { catchError, filter, map } from 'rxjs/operators';
import { UrlConfig } from '../models';
import { Observable, throwError } from 'rxjs';
import { AppConfigService } from '../../app.config.service';

@Injectable({
  providedIn: 'root',
})
export class RequesterService {
  private readonly http = inject(HttpClient);

  private appConfig = inject(AppConfigService);

  private webToken!: string;
  private webTokenExt!: string;

  setToken(token: string, external: boolean) {
    if (external) {
      this.webTokenExt = token;
    } else {
      this.webToken = token;
    }
  }

  /**
   *  {UrlConfig} config
   *  urlParams - url parameters other than search query like 'foo/' + fooId + '/boo/' + booId
   *  body
   *  params - httpParams - request params (query search, headers)
   *  showErrorAlert
   *  {Observable<HttpEvent<any>>}
   */
  request(config: UrlConfig, urlParams?: any, body?: any, params?: any): Observable<any> {
    const method = config.method;
    const url = this.getUrl(config, urlParams);

    const options = Object.assign(
      {
        reportProgress: true,
        headers: this.setHeaders(params, config.extToken),
      },
      this.setParams(params),
      RequesterService.setResponseType(params)
    );

    return this.sendRequest(new HttpRequest(method, url, body, options));
  }

  /**
   * Get url from config
   *  config {UrlConfig}
   *  urlParams
   * @return ulr
   */
  private getUrl(config: UrlConfig, urlParams?: any): string {
    let url: string;
    if (config.url.startsWith('http')) {
      url = config.url;
    } else {
      url = this.appConfig.apiUrl() + config.url;
    }

    if (urlParams) {
      Object.keys(urlParams).forEach((item) => {
        const key = `{{${item}}}`;
        if (url.indexOf(key) !== -1) {
          url = url.replace(key, urlParams[item]);
        }
      });
    }
    return url;
  }

  /**
   * Set params for request
   *  requestParams
   */
  private setParams(requestParams: any): any {
    let params = new HttpParams();

    if (requestParams) {
      if (typeof requestParams !== 'object') {
        return {};
      }

      if (requestParams.params) {
        if (requestParams.params instanceof HttpParams) {
          return { params: requestParams.params };
        }

        Object.keys(requestParams.params).forEach((item) => {
          params = params.append(item, String(requestParams.params[item]));
        });
      }
    }
    return { params };
  }

  /**
   * Set response type
   *  params
   */
  private static setResponseType(params: any): any {
    if (params) {
      if (typeof params !== 'object') {
        return {};
      }

      if (params.responseType) {
        return {
          responseType: params.responseType,
        };
      }
      return {};
    }
  }

  /**
   * Set headers for request
   *  params
   *  externalAuthorization
   */
  private setHeaders(params: any, extToken = false): any {
    let headers = new HttpHeaders();
    const headersParams = params && params.headers ? params.headers : {};
    Object.keys(headersParams).forEach(
      // HttpHeaders instance is immutable.
      (header) => {
        headers = headers.set(header, headersParams[header]);
      }
    );

    if (!params || (params && !params.skipToken)) {
      if (extToken) {
        if (this.webTokenExt) {
          headers = headers.set('WebToken', this.webTokenExt);
        }
      } else {
        if (this.webToken) {
          headers = headers.set('WebToken', this.webToken);
        }
      }
    }

    return headers;
  }

  /**
   * Send request with all params and headers
   *  request {HttpRequest}
   *  showErrorAlert
   *  showSuccessAlert Should show success alert
   */
  private sendRequest(request: HttpRequest<any>): Observable<HttpEvent<any>> {
    return this.http.request(request).pipe(
      filter((response) => response instanceof HttpResponse),
      map((response: any) => response.body),
      catchError((error: any = {}) => {
        return throwError(error);
      })
    );
  }
}
